import Tooltip from "../../ui/kit-mdtw/tooltip/Tooltip";

/**
 * Completitud de los datos de la postulación, precalculada en segundo plano por
 * cyber-service cada 5 minutos (refreshApplicationCompleteness) con las reglas
 * de la nómina de selección: solo los campos que llena la persona y que el
 * formulario de origen muestra, ajustadas por "Configurar completitud" (base,
 * empresa, cliente o área). Sin dato todavía, la celda muestra "—".
 *
 * Los campos que se comparan con la cédula tienen dos niveles: "completo"
 * (el dato existe) y "validado" (la IA, o un reclutador, confirmó que coincide
 * con el documento). El porcentaje mide lo primero; el escudo, lo segundo.
 */
export interface ICompletitudPostulacion {
  percentage: number;
  validatedPercentage: number;
  total: number;
  completed: number;
  validated: number;
  identity: { total: number; completed: number; verified: number };
  missing: string[];
  unverified: string[];
  /** Nivel de configuración más específico aplicado (AREA_NEGOCIO, CLIENTE, EMPRESA, BASE, DEFECTO). */
  rulesFrom?: string;
}

interface CompletitudView {
  label: string;
  color: string;
  bg: string;
  identityIcon: string;
  identityColor: string;
  tooltip: string;
}

// De qué nivel de "Configurar completitud" salieron las reglas aplicadas.
const RULES_FROM_LABEL: Record<string, string> = {
  AREA_NEGOCIO: "configuración del área de negocio",
  CLIENTE: "configuración del cliente",
  EMPRESA: "configuración de la empresa",
  BASE: "configuración base",
  DEFECTO: "por defecto",
};

// Cuántos campos faltantes se nombran en el tooltip antes de resumir.
const MAX_MISSING_IN_TOOLTIP = 6;

const levelColors = (percentage: number): { color: string; bg: string } => {
  if (percentage >= 100) return { color: "#1e7a45", bg: "#d1fae5" };
  if (percentage >= 80)  return { color: "#1d4ed8", bg: "#dbeafe" };
  if (percentage >= 50)  return { color: "#b45309", bg: "#fef3c7" };
  return { color: "#b91c1c", bg: "#fee2e2" };
};

const listFields = (fields: string[]): string => {
  const shown = fields.slice(0, MAX_MISSING_IN_TOOLTIP).join(", ");
  const rest = fields.length - MAX_MISSING_IN_TOOLTIP;
  return rest > 0 ? `${shown} y ${rest} más` : shown;
};

export const resolveCompletitud = (completeness?: ICompletitudPostulacion | null): CompletitudView | null => {
  if (!completeness || completeness.total === 0) return null;

  const { identity } = completeness;
  const identityVerified = identity.total > 0 && identity.verified === identity.total;
  const parts = [
    `Datos completos: ${completeness.completed} de ${completeness.total}.`,
    identityVerified
      ? "Datos de la cédula validados."
      : `Datos de la cédula validados: ${identity.verified} de ${identity.total}.`,
    `Validación total: ${completeness.validatedPercentage}%.`,
  ];
  if (completeness.missing.length > 0) parts.push(`Faltan: ${listFields(completeness.missing)}.`);
  const rulesLabel = completeness.rulesFrom ? RULES_FROM_LABEL[completeness.rulesFrom] : undefined;
  if (rulesLabel) parts.push(`Reglas: ${rulesLabel}.`);

  return {
    label: `${completeness.percentage}%`,
    ...levelColors(completeness.percentage),
    identityIcon: identityVerified ? "verified_user" : "shield",
    identityColor: identityVerified ? "#1e7a45" : "#94a3b8",
    tooltip: parts.join(" "),
  };
};

export const CompletitudPostulacionCell = ({ completeness }: { completeness?: ICompletitudPostulacion | null }) => {
  const view = resolveCompletitud(completeness);
  if (!view || !completeness) return <span style={{ color: "#94a3b8" }}>—</span>;

  return (
    <Tooltip text={view.tooltip} textTransform="none">
      <span style={{ display: "inline-flex", alignItems: "center", gap: "6px", cursor: "help", whiteSpace: "nowrap" }}>
        <span
          role="progressbar"
          aria-label="Completitud de los datos"
          aria-valuenow={completeness.percentage}
          aria-valuemin={0}
          aria-valuemax={100}
          style={{ position: "relative", width: "44px", height: "6px", borderRadius: "3px", background: view.bg, overflow: "hidden", flexShrink: 0 }}
        >
          <span style={{ position: "absolute", inset: 0, width: `${Math.min(100, Math.max(0, completeness.percentage))}%`, background: view.color, borderRadius: "3px" }} />
        </span>
        <span style={{ fontFamily: "Roboto, sans-serif", fontSize: "12.5px", fontWeight: 600, color: view.color, minWidth: "34px" }}>
          {view.label}
        </span>
        <span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: "16px", lineHeight: 1, color: view.identityColor }}>
          {view.identityIcon}
        </span>
      </span>
    </Tooltip>
  );
};
